import {XhrAppenderConfig} from './xhr-appender-config';
import {LoggerAppender} from '../logger-appender';
import {LogMessage} from '../../log-message';
import {LogLevel} from '../../log-level';
import {inject, Injectable} from '../../../di';
import {ConfigService} from '../../../config/config-service';
import {LocalStorageProvider} from '../../../storage-service';
import {filter, map} from 'rxjs';

const DEFAULT_TRIGGER_LEVEL: LogLevel = LogLevel.Critical;
const DEFAULT_CONTEXT_SIZE: number = 30;
const DEFAULT_ATTEMPT_TIMEOUT: number = 10000;

/**
 * XHR logger appender
 */
@Injectable()
export class XhrAppender extends LoggerAppender {
  private readonly configService = inject(ConfigService);

  protected config!: XhrAppenderConfig;
  protected chunksToSend: LoggerXhrChunk[] = [];

  protected attempts: number = 0;
  protected isSending: boolean = false;
  protected timeout: any | undefined = undefined;

  protected get ls(): LocalStorageProvider {
    return this.configService.config?.storage?.localStorage || window.androidStorage || localStorage;
  }

  constructor() {
    super();

    this.configService.config$
      .pipe(
        map((c) => c?.logger?.xhr),
        filter((c) => !!c),
      )
      .subscribe((c) => this.applyConfig(c));

    try {
      const lsValue = this.ls.getItem(this.config.localStorageKey);
      this.chunksToSend = lsValue ? JSON.parse(lsValue) || [] : [];
    } catch (e) {
      this.chunksToSend = [];
      console.error('[Logger] Unable to load XHR appender queue from localStorage', e);
    }

    setTimeout(() => {
      this.processQueue();
    });
  }

  private applyConfig(config?: XhrAppenderConfig) {
    // validation and fixing config:
    if (!config) {
      // Default config
      config = new XhrAppenderConfig();
    }
    if (config.triggerLevel == null) {
      config.triggerLevel = DEFAULT_TRIGGER_LEVEL;
    }
    if (config.contextSize == null || config.contextSize <= 0) {
      config.contextSize = DEFAULT_CONTEXT_SIZE;
    }
    if (config.attemptTimeout == null || config.attemptTimeout <= 0) {
      config.attemptTimeout = DEFAULT_ATTEMPT_TIMEOUT;
    }

    // applying config
    this.config = config;
  }

  log(message: LogMessage, level: LogLevel, args: any[], history: LogMessage[]): void {
    if (!this.config.enabled || level < this.config.triggerLevel) {
      return;
    }

    // store messages
    const chunk: LoggerXhrChunk = {
      message: message,
    };

    if (this.config.contextSize > 0) {
      const context = history.slice(-this.config.contextSize);
      if (context && context.length > 0) {
        chunk.context = context;
      }
    }

    this.chunksToSend.push(chunk);
    if (this.chunksToSend.length > this.config.size) {
      this.chunksToSend.shift();
    }

    this.saveChunksToLocalStorage();

    setTimeout(() => {
      this.processQueue();
    });
  }

  private processQueue() {
    if (
      !this.config.enabled ||
      !this.config.method ||
      !this.config.path ||
      this.attempts > this.config.attempts ||
      this.chunksToSend.length === 0
    ) {
      return;
    }

    if (this.timeout != null) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
    }
    this.timeout = setTimeout(() => {
      this.processQueue();
    }, this.config.attemptTimeout || DEFAULT_ATTEMPT_TIMEOUT);

    if (this.isSending) {
      return;
    }
    this.isSending = true;

    try {
      const chunk = this.chunksToSend[this.chunksToSend.length - 1];

      const xhr = new XMLHttpRequest();
      xhr.open(this.config.method, this.config.path, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      if (this.config.headers) {
        for (const headerName in this.config.headers) {
          xhr.setRequestHeader(headerName, this.config.headers[headerName]);
        }
      }
      xhr.send(JSON.stringify(chunk));
      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) {
          return;
        }
        if (xhr.status !== 200) {
          this.isSending = false;
          console.log('[Logger] Unable to send logs to server: ' + xhr.status + ' ' + xhr.statusText);
        } else {
          this.attempts++;
          this.isSending = false;
          console.log('[Logger] Logs were sent to the server');
          this.chunksToSend = this.chunksToSend.filter((e) => e === chunk);
          this.saveChunksToLocalStorage();
        }
      };
      xhr.ontimeout = () => {
        this.attempts++;
        this.isSending = false;
        console.log('[Logger] Unable to send logs to server: request timeout');
      };
    } catch (err) {
      console.log('[Logger] Unable to send logs to server', err);
      this.attempts++;
      this.isSending = false;
    }
  }

  private saveChunksToLocalStorage() {
    try {
      this.ls.setItem(this.config.localStorageKey, JSON.stringify(this.chunksToSend));
    } catch (e) {
      console.error('[Logger] Unable to save XHR appender queue to localStorage', e);
      return;
    }
  }
}

interface LoggerXhrChunk {
  message: LogMessage;
  context?: LogMessage[];
}
